if (!customElements.get('b2b-order-grid')) {
  customElements.define(
    'b2b-order-grid',
    class B2BOrderGrid extends HTMLElement {
      connectedCallback() {
        this.strings = {
          add: 'Add [count] units to cart',
          addOne: 'Add 1 unit to cart',
          addEmpty: 'Enter quantities to add',
          units: '[count] units',
          unitsOne: '1 unit',
          added: '[count] units added to your cart.',
          addedOne: '1 unit added to your cart.',
          clamped: 'You can add up to [available] of [label], so the box was set to [count].',
          clampedInCart: '[label]: [in_cart] already in your cart, up to [available] more, so the box was set to [count].',
          minimum: '[label] has a minimum of [min], so the box was set to [count].',
          step: '[label] is sold in multiples of [step], so the box was set to [count].',
          error: "Some items couldn't be added. [message]",
        };
        this.addButton = this.querySelector('[data-add]');
        this.message = this.querySelector('[data-message]');
        this.addEventListener('input', () => this.recalc());
        this.addEventListener('change', (event) => {
          const input = event.target;
          // Enter already committed this value; the change event that follows must not undo its message.
          if (input.matches('.b2b-grid__qty') && input.value !== input.dataset.committed) this.commit(input);
        });
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.addButton.addEventListener('click', this.add.bind(this));
        this.querySelector('[data-clear]').addEventListener('click', this.clear.bind(this));
        this.recalc();
      }

      get inputs() {
        return [...this.querySelectorAll('.b2b-grid__qty:not([disabled])')];
      }

      onKeydown(event) {
        if (event.key !== 'Enter' || !event.target.matches('.b2b-grid__qty')) return;
        event.preventDefault();
        this.commit(event.target);
        const inputs = this.inputs;
        const next = inputs[inputs.indexOf(event.target) + 1];
        (next || this.addButton).focus();
      }

      quantity(input) {
        const value = parseInt(input.value, 10);
        return Number.isNaN(value) || value < 0 ? 0 : value;
      }

      // Rounds the typed quantity to the variant's rules and its cap (stock or the store's per-line
      // limit, whichever is lower) minus what is already in the cart.
      commit(input) {
        const typed = this.quantity(input);
        const { min, step, max, inCart, label } = input.dataset;
        const minimum = Math.max(0, Number(min || 1) - Number(inCart));
        const increment = Number(step || 1);
        const available = Math.max(0, Number(max) - Number(inCart));
        let quantity = typed;
        let note = '';

        if (quantity > 0 && increment > 1 && quantity % increment) {
          quantity = Math.ceil(quantity / increment) * increment;
          note = this.format(this.strings.step, { label, step: increment, count: quantity });
        }
        if (quantity > 0 && quantity < minimum) {
          quantity = minimum;
          note = this.format(this.strings.minimum, { label, min: minimum, count: quantity });
        }
        if (quantity > available) {
          quantity = increment > 1 ? Math.floor(available / increment) * increment : available;
          const template = Number(inCart) > 0 ? this.strings.clampedInCart : this.strings.clamped;
          note = this.format(template, { label, count: quantity, available, in_cart: inCart });
        }

        input.value = quantity > 0 ? quantity : '';
        input.dataset.committed = input.value;
        input.closest('td').classList.toggle('is-adjusted', quantity !== typed);
        if (note) {
          this.say(note, false, input.id);
        } else if (this.message?.dataset.source === input.id) {
          this.say('');
        }
        this.recalc();
      }

      recalc() {
        let units = 0;
        let cents = 0;
        this.querySelectorAll('[data-row]').forEach((row) => {
          let rowUnits = 0;
          let rowCents = 0;
          row.querySelectorAll('.b2b-grid__qty').forEach((input) => {
            const quantity = this.quantity(input);
            rowUnits += quantity;
            rowCents += quantity * Number(input.dataset.price);
          });
          row.querySelector('[data-row-units]').textContent = rowUnits || '—';
          row.querySelector('[data-row-total]').textContent = rowUnits ? this.money(rowCents) : '—';
          units += rowUnits;
          cents += rowCents;
        });

        this.querySelectorAll('[data-item]').forEach((item) => {
          const itemUnits = [...item.querySelectorAll('.b2b-grid__qty')].reduce(
            (sum, input) => sum + this.quantity(input),
            0
          );
          item.querySelector('[data-item-units]').textContent = itemUnits
            ? this.format(this.plural('units', itemUnits), { count: itemUnits })
            : '';
        });

        this.querySelector('[data-total-units]').textContent = units;
        this.querySelector('[data-total-price]').textContent = this.money(cents);
        this.addButton.disabled = units === 0;
        this.addButton.querySelector('span').textContent =
          units === 0 ? this.strings.addEmpty : this.format(this.plural('add', units), { count: units });
      }

      clear() {
        this.inputs.forEach((input) => {
          input.value = '';
          input.closest('td').classList.remove('is-adjusted');
        });
        this.say('');
        this.recalc();
      }

      async add() {
        const picked = this.inputs.filter((input) => this.quantity(input) > 0);
        if (!picked.length || this.addButton.hasAttribute('aria-disabled')) return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = {
          items: picked.map((input) => ({ id: Number(input.dataset.variantId), quantity: this.quantity(input) })),
        };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        this.addButton.setAttribute('aria-disabled', true);
        this.addButton.classList.add('loading');
        try {
          const response = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const state = await response.json();

          // Shopify adds what it can even when it refuses part of the order, so the cart is the truth.
          const cartState = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
          this.syncInCart(cartState);
          this.inputs.forEach((input) => {
            input.value = '';
            input.closest('td').classList.remove('is-adjusted');
          });
          this.recalc();

          if (response.ok) {
            const units = body.items.reduce((sum, item) => sum + item.quantity, 0);
            this.say(this.format(this.plural('added', units), { count: units }));
          } else {
            this.say(this.format(this.strings.error, { message: state.description || state.message || '' }), true);
          }
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'b2b-order-grid', cartData: cartState });
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          this.closest('modal-dialog')?.hide();
          cart.classList.toggle('is-empty', cartState.item_count === 0);
          cart.renderContents(response.ok ? state : { sections: await this.fetchSections(cart) });
        } catch (error) {
          console.error(error);
          this.say(this.format(this.strings.error, { message: '' }), true);
        } finally {
          this.addButton.classList.remove('loading');
          this.addButton.removeAttribute('aria-disabled');
        }
      }

      async fetchSections(cart) {
        const ids = cart.getSectionsToRender().map((section) => section.id);
        return fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((r) => r.json());
      }

      syncInCart(cartState) {
        const inCart = {};
        cartState.items.forEach((item) => {
          inCart[item.variant_id] = (inCart[item.variant_id] || 0) + item.quantity;
        });
        this.querySelectorAll('.b2b-grid__qty').forEach((input) => {
          input.dataset.inCart = inCart[input.dataset.variantId] || 0;
        });
        const count = [...this.querySelectorAll('.b2b-grid__qty')].reduce(
          (sum, input) => sum + Number(input.dataset.inCart),
          0
        );
        this.querySelectorAll('[data-in-cart-count]').forEach((element) => {
          element.textContent = count;
          element.closest('[data-in-cart-note]').hidden = count === 0;
        });
      }

      say(text, isError = false, source = '') {
        if (!this.message) return;
        this.message.textContent = text;
        this.message.dataset.source = source;
        this.message.classList.toggle('is-error', isError);
      }

      money(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }

      plural(key, count) {
        return count === 1 ? this.strings[`${key}One`] : this.strings[key];
      }

      format(template, values) {
        return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template);
      }
    }
  );
}

// Loads a product's order grid into the card's "Order sizes" pop-up the first time it opens.
// modal-dialog (global.js) calls loadContent() on any .template-popup inside it.
if (!customElements.get('b2b-grid-loader')) {
  customElements.define(
    'b2b-grid-loader',
    class B2BGridLoader extends HTMLElement {
      async loadContent() {
        if (this.loaded) return;
        this.loaded = true;
        try {
          const response = await fetch(`${this.dataset.url.split('?')[0]}?section_id=b2b-order-grid`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = html.querySelector('.b2b-order__content');
          this.replaceChildren(...content.childNodes);
        } catch (error) {
          this.loaded = false;
          console.error(error);
        }
      }
    }
  );
}
